import {
  FileSessionId
} from '../FileAPIClient/Api.types';

import {
  base64ToArrayBuffer,
  arrayBufferToBase64,
} from './Cryptography';

export class FileApiSessionStorage {
    private storageKeyPrefix: string = 'fileapi_';

    public saveCryptoKeyValues(fileSessionId: FileSessionId, keyReference: string, keyPair: CryptoKeyPair): void {
        this.persistSymmetricKeyReference(fileSessionId.value, keyReference);
        this.persistCryptoKeyPair(fileSessionId.value, keyPair);
    }

    public async getCryptoKeyPair(fileSessionId: FileSessionId): Promise<CryptoKeyPair> {
        const result = await this.loadPersistedCryptoKeyPair(fileSessionId.value);
        if (!result) {
            throw new Error(`Crypto key pair not found for session: ${fileSessionId.value}`);
        }
        return result;
    }

    public getSymmetricKeyReference(fileSessionId: FileSessionId): string | null {
        try {
            if (typeof sessionStorage !== 'undefined') {
                const storedKeyRef = sessionStorage.getItem(`${this.storageKeyPrefix}${fileSessionId.value}_symmetricKeyReference`);
                if (!storedKeyRef) {
                    return null;
                }
                return storedKeyRef;
            }
        } catch (error) {
            console.warn('Failed to get symmetric key reference:', error);
            throw error;
        }
        throw new Error('Session storage is not available');
    }

    public clearDataForSession(fileSessionId: FileSessionId): void {
        try {
            if (typeof sessionStorage !== 'undefined') {
                const sessionIdValue = fileSessionId.value;
                sessionStorage.removeItem(`${this.storageKeyPrefix}${sessionIdValue}_symmetricKeyReference`);
                sessionStorage.removeItem(`${this.storageKeyPrefix}${sessionIdValue}_cryptoKeyPair_private`);
                sessionStorage.removeItem(`${this.storageKeyPrefix}${sessionIdValue}_cryptoKeyPair_public`);
            }
        } catch (error) {
            console.warn('Failed to clear persisted state for session:', error);
        }
    }

    public clearPersistedState(): void {
        if (typeof sessionStorage === 'undefined') {
            return;
        }
        
        // Find all keys that match our storage prefix and remove them
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(this.storageKeyPrefix)) {
                keysToRemove.push(key);
            }
        }
        
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
    }

    private async loadPersistedCryptoKeyPair(fileSessionId: string): Promise<CryptoKeyPair | null> {
        try {
            if (typeof sessionStorage !== 'undefined') {
                const privateKeyData = sessionStorage.getItem(`${this.storageKeyPrefix}${fileSessionId}_cryptoKeyPair_private`);
                const publicKeyData = sessionStorage.getItem(`${this.storageKeyPrefix}${fileSessionId}_cryptoKeyPair_public`);
                
                if (privateKeyData && publicKeyData) {
                    return await this.importCryptoKeyPair(privateKeyData, publicKeyData);
                }
            }
        } catch (error) {
            console.warn('Failed to load persisted crypto key pair:', error);
            this.clearDataForSession({ value: fileSessionId });
        }
        return null;
    }

    private async importCryptoKeyPair(privateKeyData: string, publicKeyData: string): Promise<CryptoKeyPair> {
        const privateKeyBuffer = await base64ToArrayBuffer(privateKeyData);
        const publicKeyBuffer = await base64ToArrayBuffer(publicKeyData);
        
        const privateKey = await window.crypto.subtle.importKey(
            'pkcs8',
            privateKeyBuffer,
            {
                name: 'RSA-OAEP',
                hash: 'SHA-1',
            },
            true,
            ['decrypt']
        );
        
        const publicKey = await window.crypto.subtle.importKey(
            'spki',
            publicKeyBuffer,
            {
                name: 'RSA-OAEP',
                hash: 'SHA-1',
            },
            true,
            ['encrypt']
        );
        
        return { privateKey, publicKey };
    }

    private persistSymmetricKeyReference(fileSessionId: string, keyReference: string): void {
        try {
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(`${this.storageKeyPrefix}${fileSessionId}_symmetricKeyReference`, keyReference);
            }
        } catch (error) {
            console.warn('Failed to persist symmetric key reference:', error);
        }
    }

    private async persistCryptoKeyPair(fileSessionId: string, keyPair: CryptoKeyPair): Promise<void> {
        try {
            if (typeof sessionStorage !== 'undefined') {
                const privateKeyBuffer = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
                const publicKeyBuffer = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
                
                const privateKeyBase64 = arrayBufferToBase64(privateKeyBuffer);
                const publicKeyBase64 = arrayBufferToBase64(publicKeyBuffer);
                
                sessionStorage.setItem(`${this.storageKeyPrefix}${fileSessionId}_cryptoKeyPair_private`, privateKeyBase64);
                sessionStorage.setItem(`${this.storageKeyPrefix}${fileSessionId}_cryptoKeyPair_public`, publicKeyBase64);
            }
        } catch (error) {
            console.warn('Failed to persist crypto key pair:', error);
        }
    }
}